import * as React from "react";
import { notification, Tabs, Checkbox, Input, InputNumber } from "antd";
import { DownloadOutlined, CopyOutlined } from "@ant-design/icons";
import { BootstrapConfig } from "../../schema/types/agent";
import { CodeEditor } from "../group/identityMatcher/codeEditor";
import { CustomScrollbars } from "../../components/Scrollbar";
import { request } from "../../services/cpln";
import { NGButton } from "../../newcomponents/button/Button";
import { CodeEditorLanguages } from "../group/identityMatcher/codeEditor";

export type ScriptTab = "userdata" | "script" | "k8s";

interface Props {
  org: string;
  agentName: string;
  bootstrapConfig: BootstrapConfig;
  onAction?: () => void;
}

export const AgentScriptTabs: React.FC<Props> = ({ org, agentName, bootstrapConfig, onAction }) => {
  const [activeTab, setActiveTab] = React.useState<ScriptTab>("userdata");
  const [k8sNamespace, setK8sNamespace] = React.useState("");
  const [k8sCreateNamespace, setK8sCreateNamespace] = React.useState(true);
  const [k8sReplicas, setK8sReplicas] = React.useState<number>(2);
  const [scriptContents, setScriptContents] = React.useState<Record<ScriptTab, string>>({
    userdata: "",
    script: "",
    k8s: "",
  });
  const [isLoadingScript, setIsLoadingScript] = React.useState(false);

  const scriptContent = scriptContents[activeTab];
  const isK8sValid = k8sNamespace.trim().length > 0 && k8sReplicas > 0;

  async function copyToClipboard(text: string, message: string = "Copied to clipboard") {
    navigator.clipboard.writeText(text);
    notification.success({ message });
    onAction?.();
  }

  function downloadFile(content: string, filename: string) {
    const file = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onAction?.();
  }

  async function fetchScript(tab: ScriptTab) {
    setIsLoadingScript(true);

    try {
      const endpoint = tab === "userdata" ? "userdata" : tab === "script" ? "script" : "k8s";
      const body: any = { bootstrap: bootstrapConfig };

      if (tab === "k8s") {
        body.namespace = k8sNamespace;
        body.createNamespace = k8sCreateNamespace;
        body.replicas = k8sReplicas;
      }

      const { data } = await request({
        method: "post",
        url: `/org/${org}/agent/-helpwith/${endpoint}`,
        body,
      });

      const content = typeof data === "string" ? data : JSON.stringify(data, null, 2);
      setScriptContents((prev) => ({ ...prev, [tab]: content }));
    } catch (e: any) {
      notification.error({
        message: "Failed to fetch script",
        description: e?.response?.data?.message || e.message,
      });
    } finally {
      setIsLoadingScript(false);
    }
  }

  React.useEffect(() => {
    if (activeTab !== "k8s" && !scriptContents[activeTab]) {
      fetchScript(activeTab);
    }
  }, [activeTab]);

  function getScriptLanguage(tab: ScriptTab): CodeEditorLanguages {
    return tab === "k8s" ? "yaml" : "shell";
  }

  function getScriptFilename(tab: ScriptTab): string {
    switch (tab) {
      case "userdata":
        return `${agentName}-userdata.sh`;
      case "script":
        return `${agentName}-startup.sh`;
      case "k8s":
        return `${agentName}-k8s.yaml`;
    }
  }

  function getTabLabel(tab: ScriptTab): string {
    switch (tab) {
      case "userdata":
        return "Userdata";
      case "script":
        return "Script";
      case "k8s":
        return "Manifests";
    }
  }

  const editorOptions = {
    basicSetup: {
      highlightActiveLine: false,
      highlightActiveLineGutter: false,
      lineNumbers: true,
      autocompletion: false,
      foldGutter: false,
    },
    readOnly: true,
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm" style={{ color: "var(--color-label)" }}>
          Download scripts for deploying your agent. The bootstrap config is automatically included.
        </div>
        <div className="flex items-center gap-2">
          <NGButton
            variant="secondary"
            size="small"
            iconPosition="left"
            renderIcon={(hover, props) => <CopyOutlined {...props} />}
            disabled={!scriptContent || isLoadingScript}
            onClick={() => copyToClipboard(scriptContent, `${getTabLabel(activeTab)} copied`)}
          >
            Copy {getTabLabel(activeTab)}
          </NGButton>
          <NGButton
            variant="secondary"
            size="small"
            iconPosition="left"
            renderIcon={(hover, props) => <DownloadOutlined {...props} />}
            disabled={!scriptContent || isLoadingScript}
            onClick={() => downloadFile(scriptContent, getScriptFilename(activeTab))}
          >
            Download {getTabLabel(activeTab)}
          </NGButton>
        </div>
      </div>

      <Tabs
        className="agent-script-tabs"
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as ScriptTab)}
        items={[
          {
            key: "userdata",
            label: "Userdata Script",
            children: isLoadingScript ? (
              <div className="p-4 text-center">Loading...</div>
            ) : scriptContents.userdata ? (
              <CustomScrollbars style={{ height: "calc(70vh - 180px)" }} className="border rounded">
                <CodeEditor
                  value={scriptContents.userdata}
                  setValue={() => {}}
                  language={getScriptLanguage("userdata")}
                  options={editorOptions}
                />
              </CustomScrollbars>
            ) : (
              <div className="p-4 text-center" style={{ color: "var(--color-label)" }}>
                No content
              </div>
            ),
          },
          {
            key: "script",
            label: "Startup Script",
            children: isLoadingScript ? (
              <div className="p-4 text-center">Loading...</div>
            ) : scriptContents.script ? (
              <CustomScrollbars style={{ height: "calc(70vh - 180px)" }} className="border rounded">
                <CodeEditor
                  value={scriptContents.script}
                  setValue={() => {}}
                  language={getScriptLanguage("script")}
                  options={editorOptions}
                />
              </CustomScrollbars>
            ) : (
              <div className="p-4 text-center" style={{ color: "var(--color-label)" }}>
                No content
              </div>
            ),
          },
          {
            key: "k8s",
            label: "K8s Manifests",
            children: (
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">
                      Namespace<span className="color-danger">*</span>
                    </span>
                    <Input
                      size="small"
                      value={k8sNamespace}
                      onChange={(e) => setK8sNamespace(e.target.value)}
                      style={{ width: 150 }}
                      placeholder="Enter namespace"
                      status={!k8sNamespace.trim() ? "error" : undefined}
                    />
                  </div>
                  <Checkbox checked={k8sCreateNamespace} onChange={(e) => setK8sCreateNamespace(e.target.checked)}>
                    <span className="text-sm">Create Namespace</span>
                  </Checkbox>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">
                      Replicas<span className="color-danger">*</span>
                    </span>
                    <InputNumber
                      size="small"
                      value={k8sReplicas}
                      onChange={(value) => setK8sReplicas(value || 0)}
                      min={1}
                      max={100}
                      style={{ width: 80 }}
                      status={k8sReplicas < 1 ? "error" : undefined}
                    />
                  </div>
                  <NGButton
                    variant="primary"
                    size="small"
                    onClick={() => fetchScript("k8s")}
                    disabled={!isK8sValid || isLoadingScript}
                    loading={isLoadingScript}
                  >
                    Generate
                  </NGButton>
                </div>
                {isLoadingScript ? (
                  <div className="p-4 text-center">Loading...</div>
                ) : scriptContents.k8s ? (
                  <CustomScrollbars style={{ height: "calc(70vh - 220px)" }} className="border rounded">
                    <CodeEditor
                      value={scriptContents.k8s}
                      setValue={() => {}}
                      language={getScriptLanguage("k8s")}
                      options={editorOptions}
                    />
                  </CustomScrollbars>
                ) : (
                  <div className="p-4 text-center" style={{ color: "var(--color-label)" }}>
                    {isK8sValid
                      ? "Click Generate to create K8s manifests"
                      : "Enter namespace and replicas, then click Generate"}
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
